import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Grid,
} from '@mui/material';
import { userAPI } from '../services/api';

const Students = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [examHistory, setExamHistory] = useState([]);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await userAPI.getStudents();
      if (response.data.success) {
        setStudents(response.data.students || []);
      } else {
        setError(response.data.message || 'Failed to load students');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching students:', error);
      setError(error.response?.data?.message || 'Failed to load students');
      setLoading(false);
    }
  };

  const handleViewHistory = async (studentId) => {
    try {
      const response = await userAPI.getStudentHistory(studentId);
      if (response.data.success) {
        setSelectedStudent(studentId);
        setExamHistory(response.data.history);
        setHistoryDialogOpen(true);
      }
    } catch (error) {
      console.error('Error fetching student history:', error);
      setError('Failed to fetch student history');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedStudent(null);
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false);
    setSelectedStudent(null);
    setExamHistory([]);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <LinearProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Student Management
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Joined Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {students.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No students found
                  </TableCell>
                </TableRow>
              ) : (
                students.map((student) => (
                  <TableRow key={student.id}>
                    <TableCell>{student.username}</TableCell>
                    <TableCell>{student.email}</TableCell>
                    <TableCell>
                      {new Date(student.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => handleViewHistory(student.id)}
                      >
                        View History
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={historyDialogOpen}
          onClose={handleCloseHistoryDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Exam History - {selectedStudent}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              View the student's exam history and performance.
            </DialogContentText>
            <Box sx={{ mt: 2 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Exam</TableCell>
                      <TableCell>Score</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Submission Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {examHistory.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No exam history found
                        </TableCell>
                      </TableRow>
                    ) : (
                      examHistory.map((result) => (
                        <TableRow key={result.id}>
                          <TableCell>{result.exam_title}</TableCell>
                          <TableCell>{result.score}%</TableCell>
                          <TableCell>
                            <Typography
                              color={result.status === 'completed' ? 'success.main' : 'warning.main'}
                            >
                              {result.status === 'completed' ? 'Completed' : 'In Progress'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {result.end_time
                              ? new Date(result.end_time).toLocaleString()
                              : 'Not submitted'}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseHistoryDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default Students; 